import { Vector3 } from 'three';
import { clamp, projectOnPlane } from '../utils/math';

const scratch = new Vector3();

/**
 * Direction the player wants to move, in world space.
 * The camera basis is projected onto the current surface so the controls stay
 * consistent no matter which surface the body is standing on.
 */
export function desiredDirection(
  move: { x: number; y: number },
  viewForward: Vector3,
  viewRight: Vector3,
  up: Vector3,
  out = new Vector3(),
): Vector3 {
  out.set(0, 0, 0);
  if (move.x === 0 && move.y === 0) return out;

  scratch.copy(viewForward).multiplyScalar(move.y).addScaledVector(viewRight, move.x);
  if (scratch.lengthSq() < 1e-8) return out.set(0, 0, 0);

  projectOnPlane(scratch, up, out);
  if (out.lengthSq() < 1e-8) return out.set(0, 0, 0);
  return out.normalize();
}

/**
 * Move `current` towards `target` by at most `maxDelta`.
 *
 * `out` may be the same object as `current`, so the direction is computed in
 * a scratch vector first.
 */
export function accelerateTowards(
  current: Vector3,
  target: Vector3,
  maxDelta: number,
  out = new Vector3(),
): Vector3 {
  scratch.copy(target).sub(current);
  const distance = scratch.length();
  if (distance <= maxDelta || distance < 1e-8) return out.copy(target);
  scratch.divideScalar(distance);
  return out.copy(current).addScaledVector(scratch, maxDelta);
}

/** Turn a heading towards a desired heading at a limited angular rate. */
export function turnToward(
  currentHeading: number,
  desiredHeading: number,
  turnRate: number,
  dt: number,
): number {
  const delta = Math.atan2(
    Math.sin(desiredHeading - currentHeading),
    Math.cos(desiredHeading - currentHeading),
  );
  const maxStep = turnRate * dt;
  return currentHeading + clamp(delta, -maxStep, maxStep);
}
